import './style.scss';

/* eslint-disable no-unused-vars */
import { Form, Input } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal/Modal';
import React from 'react';
import { AlertTriangle } from 'react-feather';
import { generatePath, useNavigate } from 'react-router-dom';

import { isCheckLoading } from '@helper/isCheckLoading';
import { useAltaIntl } from '@hook/useTranslate';
import authenticationPresenter from '@modules/authentication/presenter';
import ButtonForm from '@shared/components/ButtonForm';
import { ConfirmReStart } from '@shared/components/ConfirmReStart';
import { useSingleAsync } from '@shared/hook/useAsync';
import { useGoToCMS } from '@shared/hook/useGoToCMS';

import { ProfileSubPage } from '../..';
import { routerViewProfile } from '../../router';

interface IChangePassWord {
  isModalVisible: boolean;
  setIsModalVisible: (arg: boolean) => void;
}

const ModalChangePassWord = (props: IChangePassWord) => {
  const { isModalVisible, setIsModalVisible } = props;
  const { formatMessage } = useAltaIntl();
  const [form] = useForm();
  const navigate = useNavigate();
  const goToCMS = useGoToCMS();
  const changePassword = useSingleAsync(authenticationPresenter.changePassword);

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const onFinish = (values: any) => {
    delete values.confirmPassword;
    changePassword
      ?.execute(values)
      .then((rs: any) => {
        authenticationPresenter.getProfile().then(() => {
          handleCancel();
        });
        if (rs?.changePasswordCount === 1) {
          ConfirmReStart({
            modifiers: 'orange',
            icon: <AlertTriangle />,
            title: formatMessage('common.modal.recommend.title'),
            content: formatMessage('common.modal.recommend.text'),
            okText: formatMessage('common.modal.recommend.btn.setting'),
            cancelText: formatMessage('common.modal.recommend.btn.skip'),
            handleOk: () => {
              navigate(
                generatePath(routerViewProfile.path, { subPageParam: ProfileSubPage.SECURITY }),
              );
            },
            handleCancel: () => {
              goToCMS();
            },
          });
        }
      })
      .catch(e => console.error(e));
  };

  return (
    <Modal
      title={formatMessage('profile.change.password.title')}
      className="main-modal change-password-modal"
      open={isModalVisible}
      destroyOnClose={true}
      onCancel={handleCancel}
      closable={false}
      footer={
        <ButtonForm
          formName="form-change-pass"
          nameButtonSubmit={'common.save'}
          onCancelForm={() => handleCancel()}
          onOkForm={() => form.submit()}
          isLoading={isCheckLoading([changePassword])}
        />
      }
    >
      <Form
        className="main-form"
        layout="vertical"
        name="formChangePassword"
        form={form}
        onFinish={onFinish}
        requiredMark={false}
      >
        <Form.Item
          label={formatMessage('profile.oldPassword')}
          name="oldPassword"
          rules={[
            {
              required: true,
              message: formatMessage('profile.oldPassword.required.1'),
            },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
              message: formatMessage('profile.oldPassword.required.2'),
            },
          ]}
        >
          <Input.Password placeholder={formatMessage('profile.oldPassword')} />
        </Form.Item>

        <Form.Item
          label={formatMessage('profile.newPassword')}
          name="password"
          rules={[
            {
              required: true,
              message: formatMessage('profile.newPassword.required.1'),
            },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
              message: formatMessage('profile.newPassword.required.2'),
            },
          ]}
        >
          <Input.Password placeholder={formatMessage('profile.newPassword')} />
        </Form.Item>

        <Form.Item
          label={formatMessage('profile.confirm.newPassword')}
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            {
              required: true,
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(formatMessage('password.not.match')));
              },
            }),
          ]}
        >
          <Input.Password placeholder={formatMessage('profile.confirm.newPassword')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default React.memo(ModalChangePassWord);
